import { useMemo, useState } from 'react';
import taxonomy from '../lib/artistTaxonomy.json';
import { normalizeArtSearch } from '../lib/taxonomySearch';

const roles = taxonomy.hiringRoles;
const split = value => String(value || '').split(',').map(v => v.trim()).filter(Boolean);

export function groupHiringPreferences(values = []) {
  const groups = new Map();
  const knownRoles = new Set(Object.keys(roles));
  values.filter(Boolean).forEach(value => {
    const role = knownRoles.has(value) ? value : Object.keys(roles).find(key => roles[key].includes(value));
    const key = role || value;
    const entry = groups.get(key) || { role: key, all: false, specialisations: [] };
    if (value === key) entry.all = true;
    else if (!entry.specialisations.includes(value)) entry.specialisations.push(value);
    groups.set(key, entry);
  });
  return [...groups.values()];
}

export function HiringPreferencesSummary({ values = [] }) {
  return <div className="flex flex-wrap gap-2">{groupHiringPreferences(values).map(group =>
    <span key={group.role} className="max-w-full rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-sm text-gray-800">
      <strong>{group.role}</strong>{group.specialisations.length > 0 ? ` → ${group.specialisations.join(', ')}` : ''}
    </span>)}</div>;
}

export default function HiringPreferencePicker({ value = '', onChange }) {
  const [query, setQuery] = useState('');
  const selected = split(value);
  const filtered = useMemo(() => {
    const tokens = normalizeArtSearch(query).split(' ').filter(Boolean);
    return Object.entries(roles).map(([role, children]) => [role,
      tokens.every(t => normalizeArtSearch(role).includes(t)) ? children
        : children.filter(child => tokens.every(t => normalizeArtSearch(`${role} ${child}`).includes(t)))])
      .filter(([, children]) => children.length);
  }, [query]);
  const toggle = (role, item) => {
    let next = selected.filter(value => value !== item);
    if (!selected.includes(item)) {
      if (item === role) next = next.filter(value => !roles[role].includes(value));
      else next = next.filter(value => value !== role);
      next.push(item);
    }
    onChange([...new Set(next)].join(', '));
  };
  return <fieldset className="md:col-span-2 rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
    <legend className="px-1 font-bold">Artists this collaborator is looking for</legend>
    <p className="text-sm text-gray-600">Choose a role, then select specific specialisations—or choose any artist in that role.</p>
    <input type="search" aria-label="Search roles and specialisations" placeholder="Actor, singer, rapper, Kathak, Mehndi…" value={query} onChange={e => setQuery(e.target.value)} className="my-3 w-full rounded-lg border bg-white p-3" />
    <div className="max-h-96 space-y-2 overflow-y-auto">
      {filtered.map(([role, children]) => <details key={role} open={query.trim() ? true : undefined} className="rounded-lg border bg-white p-3">
        <summary className="cursor-pointer font-semibold">{role}{selected.includes(role) ? ' — any' : ''}{selected.filter(v => roles[role].includes(v)).length ? ` (${selected.filter(v => roles[role].includes(v)).length} selected)` : ''}</summary>
        <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={selected.includes(role)} onChange={() => toggle(role, role)} />Any {role}</label>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">{children.map(item => <label key={item} className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={selected.includes(item)} onChange={() => toggle(role, item)} />{item}</label>)}</div>
      </details>)}
      {!filtered.length && <p className="text-sm">No match. You can enter a custom requirement in the Looking for field.</p>}
    </div>
    {selected.length > 0 && <div className="mt-4"><p className="mb-2 text-sm font-semibold">Looking for</p><HiringPreferencesSummary values={selected} />
      <div className="mt-2 flex flex-wrap gap-2">{selected.map(item => <button key={item} type="button" onClick={() => onChange(selected.filter(v => v !== item).join(', '))} className="rounded border bg-white px-2 py-1 text-xs" aria-label={`Remove ${item}`}>{item} ×</button>)}</div>
    </div>}
  </fieldset>;
}
